import { useState } from 'react';
import { Modal, message } from 'antd';
import dayjs from 'dayjs';
import { vcomsatSyncApi } from '../services/api';

interface UseVehicleSyncOperationsProps {
  t: (key: string) => string;
  isMobile: boolean;
  fetchData: () => void;
}

export const useVehicleSyncOperations = ({ t, isMobile, fetchData }: UseVehicleSyncOperationsProps) => {
  const [syncingVehicles, setSyncingVehicles] = useState(false);
  const [syncingJourney, setSyncingJourney] = useState(false);
  const [journeySyncModalVisible, setJourneySyncModalVisible] = useState(false);
  const [journeyDateRange, setJourneyDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(7, 'days'),
    dayjs()
  ]);

  const handleSyncVehicles = async () => {
    setSyncingVehicles(true);
    try {
      const response = await vcomsatSyncApi.syncVehicles();
      const result = response.data;
    
      Modal.success({
        title: t('vcomsat.sync.success'),
        content: (
          <div>
            <p><strong>{t('vcomsat.total.vehicles')}:</strong> {result.totalVcomsatVehicles}</p>
            <p><strong>{t('vcomsat.synced.count')}:</strong> {result.syncedCount}</p>
            <p><strong>{t('vcomsat.matched.count')}:</strong> {result.matchedCount}</p>
            <p style={{ marginTop: 12 }}>{result.message}</p>
          </div>
        ),
        width: isMobile ? '90%' : 500
      });
      
      fetchData();
    } catch (error: any) {
      message.error(error?.response?.data?.message || error?.message || t('vcomsat.sync.failed'));
    } finally {
      setSyncingVehicles(false);
    }
  };

  const handleOpenJourneySyncModal = () => {
    setJourneySyncModalVisible(true);
  };

  const handleSyncJourneyForAllVehicles = async () => {
    if (!journeyDateRange || journeyDateRange.length !== 2) {
      message.error(t('please.select.date.range'));
      return;
    }

    setSyncingJourney(true);
    const fromDate = journeyDateRange[0].format('YYYY-MM-DD');
    const toDate = journeyDateRange[1].format('YYYY-MM-DD');

    console.log('Starting journey sync with date range:', fromDate, 'to', toDate);

    try {
      console.log('Fetching synced vehicles...');
      const syncedVehiclesResponse = await vcomsatSyncApi.getSyncedVehicles();
      const syncedVehicles = syncedVehiclesResponse.data;

      console.log('Found synced vehicles:', syncedVehicles.length, syncedVehicles);

      if (syncedVehicles.length === 0) {
        Modal.warning({
          title: t('vehicle.not.synced.with.vcomsat') || 'No Synced Vehicles',
          content: (
            <div>
              <p>No VCOMSAT vehicles found in the database.</p>
              <p><strong>Please sync vehicles first:</strong></p>
              <ol style={{ paddingLeft: '20px', marginTop: '8px' }}>
                <li>Click the "Sync Vehicles" button</li>
                <li>Wait for the sync to complete</li>
                <li>Then try syncing journey data again</li>
              </ol>
            </div>
          ),
          width: isMobile ? '90%' : 500
        });
        setSyncingJourney(false);
        return;
      }

      let successCount = 0;
      let failCount = 0;
      const results: any[] = [];

      for (const vehicle of syncedVehicles) {
        try {
          console.log(`Syncing journey for vehicle ${vehicle.carPlate} (VCOMSAT ID: ${vehicle.vcomsatCarID})...`);
      
          const response = await vcomsatSyncApi.syncJourney(
            vehicle.vcomsatCarID,
            fromDate,
            toDate
          );

          successCount++;
          results.push({
            carPlate: vehicle.carPlate,
            success: true,
            data: response.data
          });
          console.log(`Successfully synced journey for ${vehicle.carPlate}`);
        } catch (error: any) {
          failCount++;
          results.push({
            carPlate: vehicle.carPlate,
            success: false,
            error: error?.response?.data?.message || error?.message || 'Unknown error'
          });
          console.error(`Failed to sync journey for ${vehicle.carPlate}:`, error);
        }
      }

      Modal.info({
        title: t('journey.sync.completed'),
        content: (
          <div>
            <p><strong>{t('total.vehicles')}:</strong> {syncedVehicles.length}</p>
            <p style={{ color: '#52c41a' }}><strong>{t('successful.syncs')}:</strong> {successCount}</p>
            <p style={{ color: '#f5222d' }}><strong>{t('failed.syncs')}:</strong> {failCount}</p>
            <div style={{ marginTop: 12, maxHeight: 200, overflowY: 'auto' }}>
              {results.map((r, i) => (
                <div key={i} style={{ padding: '4px 0' }}>
                  <strong>{r.carPlate}:</strong>{' '}
                  {r.success ? (
                    <span style={{ color: 'green' }}>
                      ✓ {r.data.syncedJourneyDetails} journey details, {r.data.syncedStopDetails} stop details
                    </span>
                  ) : (
                    <span style={{ color: 'red' }}>✗ {r.error}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ),
        width: isMobile ? '90%' : 600
      });

      setJourneySyncModalVisible(false);
    } catch (error: any) {
      message.error(error?.response?.data?.message || error?.message || t('journey.sync.failed'));
    } finally {
      setSyncingJourney(false);
    }
  };

  return {
    syncingVehicles,
    syncingJourney,
    journeySyncModalVisible,
    journeyDateRange,
    setJourneyDateRange,
    setJourneySyncModalVisible,
    handleSyncVehicles,
    handleOpenJourneySyncModal,
    handleSyncJourneyForAllVehicles
  };
};
